import React, { useState, useRef, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import ReactCrop from 'react-image-crop';
import Switch from 'react-switch';

import Button from '../Button';
import usePhotoCrop from '../hooks/usePhotoCrop';
import { getThumbBySize } from '../../../util/photo';
import useAlbumFolderYearbookSettings from '../hooks/useAlbumFolderYearbookSettings';
import FullScreenLoader from '../FullScreenLoader';
import useYearbookPhotoMutations from '../hooks/useYearbookPhotoMutations';
import { IMAGE_SIZE_CODE } from '../../../constants/image-size-codes';

const propTypes = {
  photoId: PropTypes.string,
  identityId: PropTypes.string,
  albumFolderId: PropTypes.string,
  matchedPhotoId: PropTypes.string,
  defaultCrop: PropTypes.shape({
    x: PropTypes.number,
    y: PropTypes.number,
    width: PropTypes.number,
    height: PropTypes.number,
  }),
  defaultThumbnailSettings: PropTypes.shape({
    width: PropTypes.number,
    height: PropTypes.number,
  }),
  onClose: PropTypes.func.isRequired,
};

const PhotoCrop = ({
  photoId,
  identityId,
  albumFolderId,
  matchedPhotoId,
  defaultCrop,
  defaultThumbnailSettings,
  onClose,
}) => {
  const image = useRef(null);
  const wrapper = useRef(null);
  const [isCenteredCrop, setIsCenteredCrop] = useState(!defaultCrop);
  const { photo, loading, crop, setCrop, onFaceCenterCrop } = usePhotoCrop({
    photoId,
  });
  const thumb = getThumbBySize(photo?.thumbnail_urls, IMAGE_SIZE_CODE.LARGE2X);
  const { albumFolder } = useAlbumFolderYearbookSettings({ albumFolderId });
  const { onYearbookPhotosSet } = useYearbookPhotoMutations();
  const thumbSettings =
    defaultThumbnailSettings ??
    albumFolder?.yearbook_settings?.thumbnail_settings ??
    {};
  const aspectRatio = useMemo(() => {
    // We should crop, so we have an aspect
    if (thumbSettings && thumbSettings.width > 0) {
      return thumbSettings.width / thumbSettings.height;
    }
    // We should not crop, as width === 0
    if (thumbSettings) {
      return -1;
    }

    if (defaultCrop) {
      return defaultCrop.width / defaultCrop.height;
    }

    return -1;
  }, [thumbSettings]);

  useEffect(() => {
    if (defaultCrop) {
      setCrop({
        unit: '%',
        x: 100 * defaultCrop.x,
        y: 100 * defaultCrop.y,
        width: 100 * defaultCrop.width,
        height: 100 * defaultCrop.height,
        aspect: aspectRatio !== -1 ? aspectRatio : undefined,
      });
    }
  }, [defaultCrop, aspectRatio]);

  if (loading || !photo || (albumFolderId && !albumFolder)) {
    return <FullScreenLoader />;
  }

  return (
    <div className="crop-photo-viewer">
      <div className="crop-header-actions">
        <Button
          btnStyle="link"
          onClick={onClose}
          className="text-danger text-extra-bold"
        >
          Cancel
        </Button>
        {identityId && albumFolderId && (
          <Button
            btnStyle="link"
            className="ml-2 text-white text-extra-bold"
            onClick={async () => {
              await onYearbookPhotosSet({
                input: {
                  album_folder_id: albumFolderId,
                  yearbook_photos: [
                    {
                      identity_id: identityId,
                      photo_id: photoId,
                      facebox: {
                        x: crop.x / 100,
                        y: crop.y / 100,
                        width: crop.width / 100,
                        height: crop.height / 100,
                      },
                    },
                  ],
                },
                setMatchedPhotoId: matchedPhotoId,
              });
              onClose();
            }}
          >
            Save crop
          </Button>
        )}
      </div>
      <div className="crop-photo-viewer-image-wrapper">
        <ReactCrop
          src={thumb?.url}
          crop={crop}
          ref={wrapper}
          onChange={(c, percentCrop) => {
            setIsCenteredCrop(false);
            setCrop({
              ...percentCrop,
              aspect: aspectRatio !== -1 ? aspectRatio : undefined,
            });
          }}
          onImageLoaded={img => {
            image.current = img;
            if (!defaultCrop) {
              onFaceCenterCrop({
                container: img,
                aspectRatio: aspectRatio !== -1 ? aspectRatio : 1,
                isFullSizeFaceBox: thumbSettings.full_size_facebox,
              });
            }
          }}
          ruleOfThirds
          imageAlt="crop"
          imageStyle={{ maxWidth: '100%' }}
        />
      </div>
      {thumb && crop && (
        <div className="crop-photo-crop-settings">
          <p className="text-extra-bold mb-1">Crop dimensions</p>
          <p className="mb-1">
            Width:{' '}
            {thumbSettings?.width > 0
              ? `${Math.min(
                  Math.floor((crop.width * thumb.width) / 100),
                  thumbSettings.width,
                )}px`
              : 'Custom'}
          </p>
          <p className="mb-0">
            Height:{' '}
            {thumbSettings?.height > 0
              ? `${Math.min(
                  Math.floor((crop.height * thumb.height) / 100),
                  thumbSettings.height,
                )}px`
              : 'Custom'}
          </p>
          <hr />
          <div className="d-flex align-items-center">
            Auto size &amp; center crop{' '}
            <Switch
              id="center-crop-enabled"
              uncheckedIcon={false}
              checkedIcon={false}
              height={24}
              width={40}
              onColor="#7ace21"
              offColor="#cdd6e4"
              className="ml-2"
              onChange={() => {
                if (!isCenteredCrop) {
                  onFaceCenterCrop({
                    container: image?.current,
                    aspectRatio: aspectRatio !== -1 ? aspectRatio : 1,
                    isFullSizeFaceBox: thumbSettings.full_size_facebox,
                  });
                }
                setIsCenteredCrop(prev => !prev);
              }}
              checked={isCenteredCrop}
            />
          </div>
        </div>
      )}
    </div>
  );
};

PhotoCrop.propTypes = propTypes;

export default PhotoCrop;
